import React, { useMemo } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { ResponsiveBar } from "@nivo/bar";
import { colors as appColors } from "styles/colors";
import { NoData } from "components/NoData";

const HEIGHT = "300px";

export const PerformanceBudget = ({
	remainingTextColors,
	spentTextColors,
	offerColors,
	offerNames,
	data,
	containerStyle,
	loading,
}) => {
	const isEmpty = useMemo(
		() => data.every((obj) => Object.values(obj).every((value) => value === 0)),
		[data],
	);

	if (isEmpty) {
		return (
			<Box
				sx={{
					py: "20px",
					px: "50px",
					backgroundColor: "white.main",
					borderRadius: "20px",
					border: "1px solid #EFF0F6",
					height: HEIGHT,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					...containerStyle,
				}}
			>
				<NoData />
			</Box>
		);
	}

	if (loading) {
		return (
			<Skeleton
				variant="rounded"
				animation="wave"
				height={HEIGHT}
				width={"100%"}
				sx={{ borderRadius: "20px", ...containerStyle }}
			/>
		);
	}

	const CustomBarLabel = ({ x, y, width, height, value, color }) => {
		return (
			<text
				x={x + width / 2}
				y={y + height / 2}
				textAnchor="middle"
				dominantBaseline="middle"
				fill={color}
				fontSize="12px"
				fontWeight="500"
				fontFamily="Inter"
			>
				{Number(value).toLocaleString()}
			</text>
		);
	};

	// somehow nivo bar chart lib does not show right order in data array, so we need to use these functions
	const colorsFunction = ({ id, index }) => {
		if (id === "spent") {
			return data.length === 1
				? index === 0
					? "rgba(97, 174, 160, 0.7)"
					: "rgba(147, 139, 219, 0.7)"
				: index === 0
					? "rgba(147, 139, 219, 0.7)"
					: "rgba(97, 174, 160, 0.7)";
		}

		return data.length === 1
			? index === 0
				? "rgba(97, 174, 160, 0.2)"
				: "rgba(147, 139, 219, 0.15)"
			: index === 0
				? "rgba(147, 139, 219, 0.15)"
				: "rgba(97, 174, 160, 0.2)";
	};

	const strokeBarsFunction = (barIndex) => {
		if (data.length === 1) {
			return barIndex === 0
				? "rgba(97, 174, 160, 1)"
				: "rgba(147, 139, 219, 1)";
		}

		return barIndex === 0 ? "rgba(147, 139, 219, 1)" : "rgba(97, 174, 160, 1)";
	};

	const remainingTextColorsFunction = (barIndex) => {
		if (data.length === 1) {
			remainingTextColors[barIndex];
		}

		return [...remainingTextColors].reverse()[barIndex];
	};

	const spentTextColorsFunction = (tickIndex) => {
		if (data.length === 1) {
			spentTextColors[tickIndex];
		}

		return [...spentTextColors].reverse()[tickIndex];
	};

	return (
		<Box
			sx={{
				height: HEIGHT,
				backgroundColor: appColors.white,
				borderRadius: "20px",
				border: "1px solid #EFF0F6",
				position: "relative",
				...containerStyle,
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					p: "20px 53px 12px 73px",
					position: "absolute",
					width: "100%",
				}}
			>
				<Typography
					sx={{
						fontFamily: "Inter",
						fontSize: "16px",
						lineHeight: "20px",
						color: "#2C2C2C",
						fontWeight: "500",
					}}
				>
					Budget Spent
				</Typography>
				<Box sx={{ display: "flex", alignItems: "center" }}>
					{offerNames.map((offerName, i) => (
						<Box
							key={`${offerName}-budget`}
							sx={{ display: "flex", alignItems: "center", mr: "20px" }}
						>
							<Box
								sx={{
									width: "10px",
									height: "10px",
									borderRadius: "50%",
									backgroundColor: offerColors[i],
									mr: "6px",
								}}
							/>
							<Typography
								sx={{
									fontSize: "16px",
									lineHeight: "20px",
									color: offerColors[i],
								}}
							>
								{offerName}
							</Typography>
						</Box>
					))}
				</Box>
			</Box>
			<ResponsiveBar
				data={[...data].reverse()}
				keys={["spent", "remaining"]}
				indexBy="offer"
				layout="horizontal"
				label={null}
				margin={{ top: 52, right: 100, bottom: 20, left: 100 }}
				padding={0.3}
				layers={[
					"grid",
					"axes",
					"bars",
					({ bars }) =>
						bars.map((bar) =>
							bar.data.id === "spent" ? (
								<line
									key={bar.key}
									x1={bar.x + bar.width}
									x2={bar.x + bar.width}
									y1={bar.y}
									y2={bar.y + bar.height}
									stroke={strokeBarsFunction(bar.index)}
									strokeWidth={3}
								/>
							) : null,
						),
					({ bars }) =>
						bars.map((bar) => {
							// we're showing only remaining value inside bar chart
							if (bar.data.id === "spent") {
								return;
							}

							const color = remainingTextColorsFunction(bar.data.index);
							return (
								<CustomBarLabel
									key={bar.key}
									x={bar.x}
									y={bar.y}
									width={bar.width}
									height={bar.height}
									value={bar.data.value}
									color={color}
								/>
							);
						}),
				]}
				colors={colorsFunction}
				axisTop={null}
				axisRight={null}
				axisBottom={null}
				isInteractive={false}
				axisLeft={{
					tickSize: 0,
					renderTick: ({ tickIndex, value, x, y }) => {
						const found = data.find((d) => d.offer === value);
						const color = spentTextColorsFunction(tickIndex);

						if (found) {
							return (
								<g transform={`translate(${x},${y})`}>
									<text
										x={-5}
										y={-7}
										textAnchor="end"
										dominantBaseline="middle"
										fill={color}
										fontSize="14px"
										fontWeight="600"
										fontFamily="Inter"
									>
										{found.spent.toLocaleString()}
									</text>
									<text
										x={-5}
										y={7}
										textAnchor="end"
										dominantBaseline="middle"
										fill={color}
										fontSize="12px"
										fontWeight="400"
										fontFamily="Inter"
									>
										/{found.total.toLocaleString()}
									</text>
								</g>
							);
						}
						return null;
					},
				}}
				theme={{
					grid: {
						line: {
							stroke: "#ECECEC",
						},
					},
				}}
				enableGridX={true}
				enableGridY={true}
			/>
		</Box>
	);
};
